import React, { useState } from "react";
import get from "lodash/get";
import { Field, useFormikContext } from "formik";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AddIcon from "@mui/icons-material/Add";
import ScanditScanner from "../barcode/ScanditScanner/ScanditScanner";
import useAuth from "../../hooks/useAuth";
import SerialNumbersEditorModal from "./SerialNumbersEditorModal";

const isBlank = value => value === undefined || value === null || value === "" || value === 0;

const splitSerials = value =>
  String(value || "")
    .split(",")
    .map(v => v.trim())
    .filter(Boolean);

const joinSerials = serials => serials.map(v => String(v || "").trim()).filter(Boolean).join(", ");

const mergeUniqueSerials = (existingSerials, scanned) => {
  const next = [...(existingSerials || []).map(v => String(v || "").trim()).filter(Boolean)];
  const normalizedScanned = String(scanned || "").trim();
  if (!normalizedScanned) return next;

  if (next.some(v => v.toLowerCase() === normalizedScanned.toLowerCase())) {
    return next;
  }

  return [...next, normalizedScanned];
};

const ScannerInput = ({
  scanContainerProps,
  showNAButton = true,
  enableMultiScanFlow = false,
  multiScanLabel = "Serial Number",
  onDetected,
  ...textFieldProps
}) => {
  const { user } = useAuth();
  const { setFieldValue, values } = useFormikContext();
  const [openScan, setOpenScan] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [scanIntent, setScanIntent] = useState(null); // null | "replace" | "add"
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitialSerials, setEditorInitialSerials] = useState([]);

  const fieldName = textFieldProps.name;

  const openScanner = () => setOpenScan(true);

  const openMultiValueEditor = () => {
    setEditorInitialSerials(splitSerials(get(values, fieldName)));
    setEditorOpen(true);
  };

  const handleScannerIconClick = () => {
    if (textFieldProps.disabled) return;

    if (enableMultiScanFlow) {
      const currentValue = get(values, fieldName);
      if (!isBlank(currentValue)) {
        setChoiceDialogOpen(true);
        return;
      }
    }

    openScanner();
  };

  const startScanWithIntent = intent => {
    setScanIntent(intent);
    setChoiceDialogOpen(false);
    openScanner();
  };

  const handleDetectedCode = code => {
    if (enableMultiScanFlow && scanIntent === "replace") {
      setFieldValue(fieldName, code);
      onDetected && onDetected(code);
      setScanIntent(null);
      return;
    }

    if (enableMultiScanFlow && scanIntent === "add") {
      const existingSerials = splitSerials(get(values, fieldName));
      const merged = mergeUniqueSerials(existingSerials, code);
      setEditorInitialSerials(merged);
      setEditorOpen(true);
      setScanIntent(null);
      onDetected && onDetected(joinSerials(merged));
      return;
    }

    setFieldValue(fieldName, code);
    onDetected && onDetected(code);
  };

  const handleEditorSave = nextSerials => {
    const nextValue = joinSerials(nextSerials);
    setFieldValue(fieldName, nextValue);
    onDetected && onDetected(nextValue);
  };

  return (
    <Box
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Field
        {...textFieldProps}
        render={({ field, form, meta }) => {
          const errorMessage = meta.error || form.errors[field.name];
          const error = (meta.touched || form.submitCount > 0) && errorMessage;
          const isEmpty = isBlank(field.value);
          const showNABtn = showNAButton && isFocused && isEmpty;

          const handleNAClick = () => {
            form.setFieldValue(field.name, "N/A");
          };

          return (
            <TextField
              fullWidth
              {...field}
              value={field.value || ""}
              size="small"
              {...textFieldProps}
              autoComplete="off"
              error={!!error}
              helperText={error}
              sx={{ backgroundColor: "white" }}
              onFocus={event => {
                setIsFocused(true);
                if (textFieldProps.onFocus) textFieldProps.onFocus(event);
              }}
              onBlur={event => {
                setIsFocused(false);
                if (textFieldProps.onBlur) textFieldProps.onBlur(event);
              }}
              InputProps={{
                ...textFieldProps.InputProps,
                endAdornment: !textFieldProps.disabled && (
                  <InputAdornment position="end">
                    {showNABtn && (
                      <Chip
                        label="N/A"
                        size="small"
                        clickable
                        color="primary"
                        variant="outlined"
                        onMouseDown={handleNAClick}
                        sx={{ cursor: "pointer" }}
                      />
                    )}
                    {textFieldProps.InputProps?.endAdornment}
                    {enableMultiScanFlow && (isFocused || isHovered) && (
                      <Tooltip arrow title={`Add ${multiScanLabel}`}>
                        <IconButton size="small" onClick={openMultiValueEditor}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip arrow title="Scan Barcode">
                      <IconButton size="small" onClick={handleScannerIconClick}>
                        <QrCodeScannerIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "data-skip-navigation-effect": true,
              }}
            />
          );
        }}
      />
      {openScan && (
        <ScanditScanner
          onDetected={handleDetectedCode}
          onClose={() => {
            setOpenScan(false);
            if (enableMultiScanFlow && scanIntent === "add") {
              setEditorInitialSerials(splitSerials(get(values, fieldName)));
              setEditorOpen(true);
            }
            setScanIntent(null);
          }}
        />
      )}

      <Dialog open={choiceDialogOpen} onClose={() => setChoiceDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pr: 6,
            pb: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              color: "common.black",
              fontSize: "1.2rem",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {multiScanLabel}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setChoiceDialogOpen(false)}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            This item already has a {multiScanLabel.toLowerCase()}. What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0, justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" color="primary" onClick={() => startScanWithIntent("replace")}>
            Replace
          </Button>
          <Button variant="contained" color="primary" onClick={() => startScanWithIntent("add")}>
            Add another
          </Button>
        </DialogActions>
      </Dialog>

      <SerialNumbersEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={textFieldProps.label || multiScanLabel}
        fieldLabel={multiScanLabel}
        addButtonLabel={`+ Add ${multiScanLabel}`}
        initialSerials={editorInitialSerials}
        onSave={handleEditorSave}
      />
    </Box>
  );
};

export default ScannerInput;
